import { Add, Delete, Edit, Refresh } from "@mui/icons-material";
import {
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip
} from "@mui/material";
import { DataTable, StyledCard } from "components";
import StyledTablePagination from "components/StyledTablePagination";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { ChangeEventHandler, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IMembers } from "types";
import { getSiteLabel } from "utils";
import AddMember from "./add";
import EditMember from "./edit";
import RemoveMember from "./remove";
import { IRemoveSite } from "./types";

interface Data {
  members: IMembers[];
  totalRecords: number;
}

export default function SiteMembers({ site, onSuccess }: IRemoveSite) {
  const [isAdding, setAdding] = useState(false);
  const [editing, setEditing] = useState<IMembers>();
  const [removing, setRemoving] = useState<IMembers>();
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const t = useTranslation();
  const { isLoading, response, callFetch: callFetchGet } = useFetch<Data>("GET");
  const [members, totalRecords] = useMemo(() => {
    if (response && response.data) {
      return [response.data.members, response.data.totalRecords];
    }
    return [null, 0];
  }, [response]);
  const searchWaitRef = useRef<NodeJS.Timeout>();
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const onPageChange = (_: any, page: number) => setPage(page);
  const onRowsPerPageChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ target: { value } }) => {
    setLimit(Number.parseInt(value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!site) return;
    let params = `?limit=${limit}&page=${page + 1}&siteId=${site._id}`;
    if (searchWaitRef.current) {
      clearTimeout(searchWaitRef.current);
    }
    searchWaitRef.current = setTimeout(() => {
      callFetchGet(ENDPOINTS.members + params).catch(console.error);
    }, 300);
  }, [page, limit, reload, site, callFetchGet]);

  const onAdding = useCallback(() => {
    setAdding(false);
    onSuccess && onSuccess();
  }, [onSuccess]);

  const onEditing = useCallback(() => {
    setEditing(undefined);
    onSuccess && onSuccess();
  }, [onSuccess]);

  const onRemoving = useCallback(() => {
    setRemoving(undefined);
    onSuccess && onSuccess();
  }, [onSuccess]);

  return (
    <Fragment>
      {isAdding && <AddMember site={site} onClose={() => setAdding(false)} onSuccess={onAdding} />}
      {editing && (
        <EditMember member={editing} onClose={() => setEditing(undefined)} onSuccess={onEditing} />
      )}
      {removing && (
        <RemoveMember
          member={removing}
          onSuccess={onRemoving}
          onClose={() => setRemoving(undefined)}
        />
      )}
      <StyledCard>
        <CardHeader
          title={useMemo(() => `${t["Members"]} ${site && getSiteLabel(site)}`, [site, t])}
          action={
            <Stack direction="row" spacing={1}>
              <Tooltip title={t["Add Member"]}>
                <IconButton onClick={() => setAdding(true)}>
                  <Add color="secondary" />
                </IconButton>
              </Tooltip>
              {isLoading ? (
                <IconButton disabled>
                  <CircularProgress />
                </IconButton>
              ) : (
                <Tooltip title={t["Refresh"]}>
                  <IconButton onClick={onReload}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          <DataTable
            rows={members}
            columns={[
              {
                header: t["Name"],
                accessor: (row) => row?.user?.name
              },
              {
                header: t["Email"],
                accessor: (row) => row?.user?.email
              },
              {
                header: t["Role"],
                accessor: "role"
              },
              {
                header: t["Actions"],
                align: "center",
                accessor: (row) => (
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title={t["Edit"]}>
                      <IconButton onClick={() => setEditing(row)}>
                        <Edit color="info" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t["Delete"]}>
                      <IconButton onClick={() => setRemoving(row)}>
                        <Delete color="error" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )
              }
            ]}
          />
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end" }}>
          <StyledTablePagination
            component="div"
            align="right"
            count={totalRecords || 0}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={limit}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </CardActions>
      </StyledCard>
    </Fragment>
  );
}
